import type {GameArm} from "./gameArm";
import type {GameId} from "./gameCatalog";

/**
 * The transfer-game rules that sit AROUND a game (kitecorp/transferify-ui#2872, #2874) — React-free.
 *
 * The epic's shared slot: when a transfer is long enough to offer a game, who sees the offer, what
 * happens to the success card when the transfer finishes mid-game, and what the A/B test reports. The
 * games themselves (`./byteRunner`, `./fileInvaders`, `./packTheZip`) know none of these rules: an
 * engine only says where its run stands ({@link RunPhase}), and this module decides what that reports.
 */

/** A transfer that will take at least this long offers the game (owner decision 2026-09-24). */
export const GAME_MIN_TRANSFER_SECONDS = 20;

/**
 * Where the game is offered: the hosted upload's progress card, the Instant (P2P) live card, or the
 * recipient's download page (kitecorp/transferify-ui#2889). It is also the `mode` of every game event, so
 * the recipient page's rounds never mix into the senders' test.
 */
export type GameMode = "upload" | "p2p" | "download";

/**
 * Whether a transfer offers the game, decided ONCE from the first ETA sample: a hosted upload's, and since
 * kitecorp/transferify-ui#2889 a recipient's download.
 *
 * `previous` is the decision already taken (or null before one), so the rule can be folded over every
 * readout: after the first real sample the answer never changes, which is what keeps the button from
 * flickering as the ETA counts down past 20 seconds. A sample that is not a finite number (the model's
 * stall floor) is not a sample.
 */
export function decideEtaEligibility(previous: boolean | null, etaSeconds: number | null): boolean | null {
	if (previous !== null) {
		return previous;
	}
	if (etaSeconds === null || !Number.isFinite(etaSeconds)) {
		return null;
	}
	return etaSeconds >= GAME_MIN_TRANSFER_SECONDS;
}

/**
 * Where a hosted transfer stands for its game slot (kitecorp/transferify-ui#3388): `sending` while its upload
 * runs and while its recipients' emails are retried, `sent` on its success card, and `none` on every other
 * screen. One slot spans `sending` and `sent`, so the success card offers the game the upload picked, and the
 * transfer reports one offer.
 */
export type HostedGameStage = "none" | "sending" | "sent";

/**
 * Whether a hosted transfer offers the game. While it is being sent, its first ETA sample decides
 * ({@link decideEtaEligibility}). Its success card offers the game whatever that decided, as the Instant live
 * card offers it from the start (kitecorp/transferify-ui#3388): the upload is over, so there is no wait left to
 * judge it by.
 */
export function hostedGameEligibility(stage: HostedGameStage, etaDecision: boolean | null): boolean | null {
	switch (stage) {
		case "sending":
			return etaDecision;
		case "sent":
			return true;
		case "none":
			return null;
	}
}

/** The control group never sees the button; the game group sees it on an eligible transfer. */
export function offersPlay(arm: GameArm, eligible: boolean | null): boolean {
	return arm === "game" && eligible === true;
}

/**
 * What the upload's completion does to the screen. A sender who is IN the game when the upload
 * finishes stays there until they choose to continue; everyone else gets the success card exactly as
 * before the game existed.
 */
export function completionAction(gameOpen: boolean): "hold" | "continue" {
	return gameOpen ? "hold" : "continue";
}

/** How one run or round ended: lost (a crash, a breached firewall), finished at 100%, or the × while playing. */
export type RunOutcome = "crashed" | "finished" | "closed";

/**
 * How a run ends on its own, as a game's engine reports it. `closed` is not one: only the ×
 * produces it, through {@link closeOutcome}, so a game cannot decide on its own what a close reports.
 */
export type EndedOutcome = Exclude<RunOutcome, "closed">;

/**
 * Where a game's run stands when the × is pressed: no run in progress (before the first, between
 * runs, or after the finish), a round being played, or the short end sequence that plays after the
 * transfer completed (Byte Runner's flag run, File Invaders' flee, Pack the Zip's zip seal).
 */
export type RunPhase = "none" | "playing" | "ending";

/**
 * What the × reports for the run it interrupts, or null when there is none. A close during the end
 * sequence is `finished`: that round was ended by the transfer completing, not by the player quitting
 * it (kitecorp/transferify-ui#2890, decided 2026-09-25).
 */
export function closeOutcome(phase: RunPhase): RunOutcome | null {
	switch (phase) {
		case "playing":
			return "closed";
		case "ending":
			return "finished";
		case "none":
			return null;
	}
}

const SCORE_BUCKETS: ReadonlyArray<readonly [number, string]> = [
	[100, "0-99"],
	[250, "100-249"],
	[500, "250-499"],
	[1000, "500-999"],
	[2500, "1000-2499"],
];

/** A range rather than the raw score: the test needs engagement, not a leaderboard. */
export function scoreBucket(score: number): string {
	return SCORE_BUCKETS.find(([below]) => score < below)?.[1] ?? "2500+";
}

/** Parameters of the four GA4 game events, as the api accepts them (kitecorp/transferify-api-service#2102). */
export type GameEventParams = Readonly<Record<string, string>>;

export interface GameReport {
	/** `GAME_OFFERED`, once per transfer, in BOTH groups: the A/B denominator. Null when already sent. */
	offered(): GameEventParams | null;
	/** `GAME_OPENED`, on every press of Play. */
	opened(): GameEventParams;
	/**
	 * `GAME_RUN_FINISHED` for a run that ended on its own. Null once the game was closed, until it is
	 * opened again: the closed game's last frame can still end the run the × already reported.
	 */
	runFinished(outcome: EndedOutcome, score: number): GameEventParams | null;
	/**
	 * `GAME_RUN_FINISHED` for the run the × interrupted ({@link closeOutcome}), or null when there was
	 * none or the game was already closed. Either way the game is closed from here on.
	 */
	closed(phase: RunPhase, score: number): GameEventParams | null;
	/**
	 * `GAME_TRANSFER_COMPLETED`, once, and only for a transfer that was offered the game: the completion
	 * rate is completions over offers, so a completion without an offer has no denominator.
	 */
	completed(): GameEventParams | null;
}

/** One transfer's reporting state, for the game it picked. Create a new one per transfer. */
export function createGameReport(mode: GameMode, arm: GameArm, game: GameId): GameReport {
	const base = {game, mode};
	let offered = false;
	let completed = false;
	let open = false;
	const runEvent = (outcome: RunOutcome, score: number): GameEventParams => ({...base, outcome, score_bucket: scoreBucket(score)});
	return {
		offered() {
			if (offered) {
				return null;
			}
			offered = true;
			return {...base, variant: arm};
		},
		opened() {
			open = true;
			return {...base};
		},
		runFinished: (outcome, score) => (open ? runEvent(outcome, score) : null),
		closed(phase, score) {
			const outcome = open ? closeOutcome(phase) : null;
			open = false;
			return outcome === null ? null : runEvent(outcome, score);
		},
		completed() {
			if (!offered || completed) {
				return null;
			}
			completed = true;
			return {...base, variant: arm};
		},
	};
}
